import { setHtml } from './css.js';
export { PALETTE, chartFromRange, sparklineSvg } from './chart-core.js';
import { PALETTE } from './chart-core.js';
import { safeColor } from './cellfx.js';
const esc = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const fmt = (v, d) => (d != null ? v.toFixed(d) : Math.abs(v) >= 100 ? Math.round(v).toLocaleString('ko-KR') : String(Math.round(v * 100) / 100));
/** 보기 좋은 눈금 (1·2·5 × 10^n) */
function niceTicks(min, max, count = 5) {
    if (min === max) {
        max = min + 1;
    }
    const span = max - min;
    const step0 = span / count;
    const mag = Math.pow(10, Math.floor(Math.log10(step0)));
    const step = [1, 2, 5, 10].map(m => m * mag).find(s => s >= step0);
    const lo = Math.floor(min / step) * step, hi = Math.ceil(max / step) * step;
    const out = [];
    for (let v = lo; v <= hi + step / 2; v += step)
        out.push(+v.toFixed(10));
    return out;
}
export function chartSvg(spec) {
    const W = spec.width ?? 640, H = spec.height ?? 320;
    const series = spec.series.map((s, i) => ({ ...s, name: String(s.name ?? ''), color: safeColor(s.color) ?? PALETTE[i % PALETTE.length] }));
    const titleH = spec.title ? 24 : 0;
    const legendH = series.length > 1 || spec.type === 'pie' ? 22 : 0;
    const parts = [`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" class="og-chart" font-family="Malgun Gothic, Apple SD Gothic Neo, sans-serif" font-size="11" role="img" aria-label="${esc(spec.title ?? '차트')}">`];
    if (spec.title)
        parts.push(`<text x="${W / 2}" y="16" text-anchor="middle" font-size="13" font-weight="600" style="fill:var(--og-fg,#1b2833)">${esc(spec.title)}</text>`);
    const legendItems = spec.type === 'pie' ? spec.labels.map((l, i) => ({ name: String(l ?? ''), color: PALETTE[i % PALETTE.length] })) : series;
    if (legendH) {
        let x = 8;
        legendItems.slice(0, 12).forEach(s => { parts.push(`<rect x="${x}" y="${H - 15}" width="10" height="10" fill="${s.color}" rx="2"/><text x="${x + 14}" y="${H - 6}" style="fill:var(--og-fg2,#4a5a66)">${esc(s.name)}</text>`); x += 24 + s.name.length * 11; });
    }
    if (spec.type === 'pie') {
        const vals = series[0]?.values.map(v => Math.max(0, v ?? 0)) ?? [];
        const total = vals.reduce((a, b) => a + b, 0) || 1;
        const cx = W / 2, cy = titleH + (H - titleH - legendH) / 2, r = Math.min(W, H - titleH - legendH) / 2 - 10;
        let a0 = -Math.PI / 2;
        vals.forEach((v, i) => {
            const a1 = a0 + (v / total) * Math.PI * 2;
            const large = a1 - a0 > Math.PI ? 1 : 0;
            const p = (a) => `${cx + r * Math.cos(a)},${cy + r * Math.sin(a)}`;
            const d = vals.length === 1 || v === total ? `M${cx - r},${cy}a${r},${r} 0 1,0 ${2 * r},0a${r},${r} 0 1,0 ${-2 * r},0` : `M${cx},${cy}L${p(a0)}A${r},${r} 0 ${large} 1 ${p(a1)}Z`;
            parts.push(`<path d="${d}" fill="${PALETTE[i % PALETTE.length]}" style="stroke:var(--og-bg,#fff)" stroke-width="1"><title>${esc(spec.labels[i])}: ${fmt(v, spec.decimals)} (${((v / total) * 100).toFixed(1)}%)</title></path>`);
            if (v / total > 0.05) {
                const am = (a0 + a1) / 2;
                parts.push(`<text x="${cx + r * 0.65 * Math.cos(am)}" y="${cy + r * 0.65 * Math.sin(am) + 4}" text-anchor="middle" fill="#fff" font-weight="600">${((v / total) * 100).toFixed(0)}%</text>`);
            }
            a0 = a1;
        });
        parts.push('</svg>');
        return parts.join('');
    }
    const horiz = spec.type === 'hbar';
    const n = spec.labels.length;
    // 값 범위
    let lo = 0, hi = 0;
    if (spec.stacked && spec.type !== 'line') {
        for (let i = 0; i < n; i++) {
            let pos = 0, neg = 0;
            for (const s of series) {
                const v = s.values[i] ?? 0;
                v >= 0 ? (pos += v) : (neg += v);
            }
            hi = Math.max(hi, pos);
            lo = Math.min(lo, neg);
        }
    }
    else
        for (const s of series)
            for (const v of s.values)
                if (v != null) {
                    hi = Math.max(hi, v);
                    lo = Math.min(lo, v);
                }
    for (const r of spec.refLines ?? []) {
        hi = Math.max(hi, r.value);
        lo = Math.min(lo, r.value);
    }
    if (spec.type === 'line' && lo > 0)
        lo = Math.min(...series.flatMap(s => s.values.filter((v) => v != null)), hi) * 0.9;
    const ticks = niceTicks(lo, hi);
    lo = ticks[0];
    hi = ticks[ticks.length - 1];
    const maxLabel = Math.min(16, Math.max(...spec.labels.map(l => l.length), 1));
    const padL = horiz ? 12 + maxLabel * 11 : 48, padR = 16, padT = titleH + 10, padB = legendH + (horiz ? 22 : 34);
    const pw = W - padL - padR, ph = H - padT - padB;
    const scale = (v) => (v - lo) / (hi - lo || 1);
    // 격자·눈금
    for (const t of ticks) {
        if (horiz) {
            const x = padL + scale(t) * pw;
            parts.push(`<line x1="${x}" y1="${padT}" x2="${x}" y2="${padT + ph}" style="stroke:var(--og-line,#e6ebef)"/><text x="${x}" y="${padT + ph + 14}" text-anchor="middle" style="fill:var(--og-muted,#6b7a86)">${fmt(t)}</text>`);
        }
        else {
            const y = padT + ph - scale(t) * ph;
            parts.push(`<line x1="${padL}" y1="${y}" x2="${padL + pw}" y2="${y}" style="stroke:var(--og-line,#e6ebef)"/><text x="${padL - 6}" y="${y + 4}" text-anchor="end" style="fill:var(--og-muted,#6b7a86)">${fmt(t)}</text>`);
        }
    }
    const band = (horiz ? ph : pw) / Math.max(1, n);
    const zero = scale(Math.max(lo, Math.min(hi, 0)));
    // 범주 라벨 (많으면 건너뜀)
    const every = Math.max(1, Math.ceil(n / (horiz ? ph / 14 : pw / 48)));
    spec.labels.forEach((l, i) => {
        if (i % every)
            return;
        const short = l.length > 14 ? l.slice(0, 13) + '…' : l;
        if (horiz)
            parts.push(`<text x="${padL - 6}" y="${padT + band * (i + 0.5) + 4}" text-anchor="end" style="fill:var(--og-fg2,#33424d)">${esc(short)}</text>`);
        else
            parts.push(`<text x="${padL + band * (i + 0.5)}" y="${padT + ph + 16}" text-anchor="middle" style="fill:var(--og-fg2,#33424d)">${esc(short)}</text>`);
    });
    if (spec.type === 'line') {
        series.forEach(s => {
            const pts = [];
            s.values.forEach((v, i) => { if (v == null)
                return; pts.push(`${padL + band * (i + 0.5)},${padT + ph - scale(v) * ph}`); });
            parts.push(`<polyline points="${pts.join(' ')}" fill="none" stroke="${s.color}" stroke-width="2"/>`);
            s.values.forEach((v, i) => { if (v == null)
                return; parts.push(`<circle cx="${padL + band * (i + 0.5)}" cy="${padT + ph - scale(v) * ph}" r="3" fill="${s.color}"><title>${esc(spec.labels[i])} · ${esc(s.name)}: ${fmt(v, spec.decimals)}</title></circle>`); });
        });
    }
    else {
        const groups = spec.stacked ? 1 : series.length;
        const bw = Math.max(2, (band * 0.75) / groups);
        for (let i = 0; i < n; i++) {
            let accPos = 0, accNeg = 0;
            series.forEach((s, k) => {
                const v = s.values[i];
                if (v == null)
                    return;
                let a, b;
                if (spec.stacked) {
                    if (v >= 0) {
                        a = accPos;
                        b = accPos + v;
                        accPos = b;
                    }
                    else {
                        a = accNeg + v;
                        b = accNeg;
                        accNeg = a;
                    }
                }
                else {
                    a = Math.min(0, v);
                    b = Math.max(0, v);
                }
                const off = band * 0.125 + (spec.stacked ? 0 : k * bw);
                const tip = `<title>${esc(spec.labels[i])} · ${esc(s.name)}: ${fmt(v, spec.decimals)}</title>`;
                if (horiz) {
                    const x = padL + scale(Math.max(lo, a)) * pw, w = (scale(b) - scale(Math.max(lo, a))) * pw;
                    parts.push(`<rect class="og-bar" x="${x}" y="${padT + band * i + off}" width="${Math.max(0, w)}" height="${bw}" fill="${s.color}" rx="1">${tip}</rect>`);
                }
                else {
                    const y = padT + ph - scale(b) * ph, h = (scale(b) - scale(Math.max(lo, a))) * ph;
                    parts.push(`<rect class="og-bar" x="${padL + band * i + off}" y="${y}" width="${bw}" height="${Math.max(0, h)}" fill="${s.color}" rx="1">${tip}</rect>`);
                }
            });
        }
    }
    // 0 축·기준선
    if (horiz)
        parts.push(`<line x1="${padL + zero * pw}" y1="${padT}" x2="${padL + zero * pw}" y2="${padT + ph}" style="stroke:var(--og-muted,#8c99a6)"/>`);
    else
        parts.push(`<line x1="${padL}" y1="${padT + ph - zero * ph}" x2="${padL + pw}" y2="${padT + ph - zero * ph}" style="stroke:var(--og-muted,#8c99a6)"/>`);
    for (const r of spec.refLines ?? []) {
        const c = safeColor(r.color) ?? '#d1242f';
        if (horiz) {
            const x = padL + scale(r.value) * pw;
            parts.push(`<line x1="${x}" y1="${padT}" x2="${x}" y2="${padT + ph}" stroke="${c}" stroke-dasharray="4 3"/><text x="${x + 3}" y="${padT + 10}" fill="${c}">${esc(r.label ?? fmt(r.value))}</text>`);
        }
        else {
            const y = padT + ph - scale(r.value) * ph;
            parts.push(`<line x1="${padL}" y1="${y}" x2="${padL + pw}" y2="${y}" stroke="${c}" stroke-dasharray="4 3"/><text x="${padL + pw - 2}" y="${y - 3}" text-anchor="end" fill="${c}">${esc(r.label ?? fmt(r.value))}</text>`);
        }
    }
    parts.push('</svg>');
    return parts.join('');
}
export function renderChart(el, spec) {
    const w = spec.width ?? Math.max(240, el.clientWidth || 640);
    setHtml(el, chartSvg({ ...spec, width: w }));
}
/** 그리드 → 차트 데이터. rows: 'view'(필터·정렬 결과) | 'range'(선택 범위) */
export function chartFromGrid(grid, o) {
    const rows = o.rows === 'range' ? grid.getRangeRows() : Array.from(grid.getViewRows());
    const lim = rows.slice(0, o.limit ?? 50);
    const col = (id) => grid.opts.columns.find(c => c.id === id);
    return {
        type: o.type ?? 'bar',
        title: o.title,
        labels: lim.map(r => grid.cellText(r, o.labelCol)),
        series: o.valueCols.map(id => ({ name: col(id)?.header ?? id, values: lim.map(r => { const v = grid.cellValue(r, id); return typeof v === 'number' ? v : v == null ? null : Number(v); }) })),
    };
}
